import {
  BASE_SALG,
  KAMPANJE_SALGSMULT_CAP,
  MILESTONE_GRENSER,
  SALG_DECAY,
  SALG_INGEN_ANMELDELSE_BOOST,
  TIER_SALG_FAKTOR,
} from '@/config';
import { finnKanal, sjangerTreffFor } from '@/data/kanaler';
import { SPILLER_TIER_SALGSBONUS } from '@/systems/tier';
import { sesongSalgFaktor } from '@/systems/sesong';
import type { ContractedBook } from '@/data/types';
import type { AktivKampanje, Tier } from '@/state/GameState';

export type SalgResultat = {
  dagssalg: number;
  inntekt: number;
  nyeMilestonerNaadd: number[];
};

export function beregnDagssalg(
  bok: ContractedBook,
  dag: number,
  aktiveKampanjer: ReadonlyArray<AktivKampanje> = [],
  spillerTier: Tier = 'D',
  oppgraderingFaktor = 1,
): number {
  if (bok.status !== 'utgitt') return 0;
  if (bok.utgivelsesDag === null) return 0;

  const tierFaktor = TIER_SALG_FAKTOR[bok.tier];
  const spillerTierFaktor = SPILLER_TIER_SALGSBONUS[spillerTier];
  const anmeldelsesBoost = beregnAnmeldelsesBoost(bok);
  const dagerSiden = Math.max(0, dag - bok.utgivelsesDag);
  const decayFaktor = beregnDecayFaktor(bok, dagerSiden);
  const kampanjeFaktor = beregnKampanjeFaktor(bok, dag, aktiveKampanjer);
  const sesongFaktor = sesongSalgFaktor(dag, bok.genre);

  const raw =
    BASE_SALG *
    tierFaktor *
    spillerTierFaktor *
    anmeldelsesBoost *
    decayFaktor *
    kampanjeFaktor *
    sesongFaktor *
    oppgraderingFaktor;
  return Math.max(0, Math.floor(raw));
}

export function beregnKampanjeFaktor(
  bok: ContractedBook,
  dag: number,
  aktiveKampanjer: ReadonlyArray<AktivKampanje>,
): number {
  let faktor = 1.0;
  for (const k of aktiveKampanjer) {
    if (k.isbn !== bok.isbn) continue;
    if (dag < k.startDag || dag > k.ferdigDag) continue;
    const kanal = finnKanal(k.kanalId);
    if (!kanal) continue;
    const treff = sjangerTreffFor(kanal, bok.genre);
    faktor *= kanal.effekt.salgsMultiplikator * treff;
  }
  if (faktor > KAMPANJE_SALGSMULT_CAP) faktor = KAMPANJE_SALGSMULT_CAP;
  if (faktor < 1.0) faktor = 1.0;
  return faktor;
}

export function beregnAnmeldelsesBoost(bok: ContractedBook): number {
  if (bok.anmeldelser.length === 0) return SALG_INGEN_ANMELDELSE_BOOST;
  const snitt =
    bok.anmeldelser.reduce((s, a) => s + a.terninger, 0) / bok.anmeldelser.length;
  return snitt / 3.5;
}

export function beregnDecayFaktor(bok: ContractedBook, dagerSiden: number): number {
  const snitt =
    bok.anmeldelser.length === 0
      ? 3.0
      : bok.anmeldelser.reduce((s, a) => s + a.terninger, 0) / bok.anmeldelser.length;
  let decay = SALG_DECAY;
  let gulv = 0.1;
  if (snitt < 2) {
    decay = 0.07;
    gulv = 0.05;
  } else if (snitt < 3) {
    decay = 0.05;
    gulv = 0.1;
  } else if (snitt < 4) {
    decay = 0.04;
    gulv = 0.15;
  } else if (snitt < 5) {
    decay = 0.025;
    gulv = 0.25;
  } else {
    decay = 0.015;
    gulv = 0.35;
  }
  return Math.max(gulv, 1.0 - decay * dagerSiden);
}

export function finnNyeMilestoner(
  totaltSalgFør: number,
  totaltSalgEtter: number,
  allerede: ReadonlyArray<number>,
): number[] {
  const nye: number[] = [];
  for (const m of MILESTONE_GRENSER) {
    if (allerede.includes(m.tall)) continue;
    if (totaltSalgFør < m.tall && totaltSalgEtter >= m.tall) {
      nye.push(m.tall);
    }
  }
  return nye;
}

export function milestoneFor(tall: number): string {
  const m = MILESTONE_GRENSER.find((x) => x.tall === tall);
  return m?.melding ?? `har nådd ${tall.toLocaleString('nb-NO')} solgte`;
}
